import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router';
import { MarketType, MarketTypeList } from '../../@types/api';
import { MARKET_TYPE_USED_FOR_OPTIONS, QUERY_KEYS } from '../../constants';
import { useModal } from '../../contexts/ModalContext';
import {
  createCashOutColumn,
  createDescriptionColumn,
  createEditColumn,
  createIsActiveColumn,
  createSportIconColumn,
} from '../../helpers/columns';
import { defaultColumnsMarkets } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../utils';
import FormModal from '../modals/FormModal';
import TableTemplate from '../templates/TableTemplate';
import usePermissions from '../../hooks/usePermissions';
import BetBuilderBadge from '../atoms/BetBuilderBadge';

const MarketTypesPage = () => {
  const {
    data: marketTypesData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<MarketTypeList>('market-types');
  const { deleteData, updateData } = useMutateData('market-types', [QUERY_KEYS.marketTypes]);
  const { handleSort } = useSort(changeQuery);
  const { openModal } = useModal();
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const showCashout = hasPermission('manageCashOut');

  const addOutcome = (marketId: string, sportId: string) => {
    navigate('../outcomes', {
      state: {
        defaultValues: {
          sportId,
          marketTypeId: marketId,
        },
        defaultFilters: {
          isActive: true,
          language: 'en',
          sportIds: [sportId],
          marketTypeIds: [marketId],
        },
      },
    });
    openModal(<FormModal form='outcomes' />);
  };

  const columns: GridColDef[] = [
    createColumn('id', 'ID', { sortable: false }),
    createColumn('sportName', 'Sport', {
      valueGetter: (value, row, column, apiRef) => row.sport.name,
    }),
    createSportIconColumn<MarketType>(),
    createColumn('name', 'Market Name', {
      minWidth: 200,
      valueGetter: (value, row, column, apiRef) => {
        const { name, eventPartName } = row;
        return eventPartName ? `${name}, ${eventPartName}` : name;
      },
    }),
    createColumn('shortName', 'Market Short Name'),
    createColumn('usedFor', 'Used For', {
      valueGetter: (value, row, column, apiRef) => {
        const usedFor = row.usedFor;
        return usedFor
          .map(
            (usedFor: MarketTypeList['usedFor'][number]) =>
              MARKET_TYPE_USED_FOR_OPTIONS.find((option) => option.id === usedFor)?.name
          )
          .join(', ');
      },
      sortable: false,
    }),
    createColumn('type', 'Type', {
      valueGetter: (value, row, column, apiRef) => formatSnakeCaseString(row.type, false),
      sortable: false,
    }),
    createColumn('position', 'Position'),
    createDescriptionColumn(),
    createIsActiveColumn<MarketType>({
      updateData,
    }),
    ...(showCashout ? [createCashOutColumn<MarketType>({ updateData, modalEntity: 'market' })] : []),
    createColumn('betBuilder', 'Bet Builder', {
      renderCell: (params: GridRenderCellParams<MarketType>) => <BetBuilderBadge enabled={params.row.betBuilder} />,
      sortable: false,
    }),
    createEditColumn({
      deleteData,
      addOutcome,
      minWidth: 150,
      columnName: 'Actions',
    }),
  ];

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  return (
    <TableTemplate
      rows={marketTypesData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={marketTypesData?.count || 0}
      createItemBtnLabel='Add new market'
      defaultVisibleColumns={defaultColumnsMarkets}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      formType='markets'
      showExport
    />
  );
};

export default MarketTypesPage;
